import { useCallback } from 'react';
import { Upload } from 'lucide-react';
import { ALLOWED_JOB_FILE_CONTENT_TYPES, MAX_JOB_FILE_BATCH } from '@jobos/shared';

import { FileDropZone } from '@/components/shared/FileDropZone';
import { useAcceptedJobFiles } from './useAcceptedJobFiles';

// The Files tab's drop target. Type filtering happens in front of the queue, so
// a rejected file never occupies an upload slot — the same division
// ReceiptDropZone uses. The batch size limit stays in the queue, which owns it.

const ACCEPT = ['image/*', ...ALLOWED_JOB_FILE_CONTENT_TYPES].join(',');

interface JobFileUploadPanelProps {
  /** The accepted files. Where they land is the open folder, which the section owns. */
  onFiles: (files: File[]) => void;
  /** Named so the drop zone can say where the files are going. */
  folderName?: string;
}

export function JobFileUploadPanel({ onFiles, folderName }: JobFileUploadPanelProps) {
  const acceptFiles = useAcceptedJobFiles();

  const handleFiles = useCallback(
    (files: File[]) => {
      const accepted = acceptFiles(files);
      if (accepted.length === 0) return;
      onFiles(accepted);
    },
    [acceptFiles, onFiles],
  );

  return (
    <div className="space-y-3 rounded-lg border bg-gray-50 p-4">
      {/* Never put in `busy` mode: uploading in the background is the point of the
          two-phase queue, and a second drop simply chains behind the first. */}
      <FileDropZone onFiles={handleFiles} accept={ACCEPT}>
        <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400" />
        <p className="font-semibold text-gray-700">Drop files here or tap to browse</p>
        <p className="mt-1 text-sm text-gray-400">
          Images, PDF, Word or Excel — up to {MAX_JOB_FILE_BATCH} at a time
        </p>
        {/* Where the drop lands, said before it happens rather than discovered
            after: the open folder is the destination, not a picker. */}
        <p className="mt-1 text-sm text-gray-500">
          Uploading to {folderName ?? 'All files'}
        </p>
      </FileDropZone>
    </div>
  );
}
